import React, { useState } from 'react';
import * as Material from '@mui/material';
import { IconEye, IconEyeOff, IconBuildingBank, IconMoneybag, IconWallet, IconExternalLink } from '@tabler/icons-react';
import { Button, Text } from '@fragments';
import Parser from '@helpers/parser.js';
import {
    useWallets,
    walletBalanceCurrency,
    walletCanWithdraw,
    walletHeadlineBalance,
    walletIsConnected,
    walletWithdrawalPath,
} from '@helpers/wallets';
import { useCommunity } from '@contexts/CommunityContext';
import { ReactComponent as BrazilIcon } from '@assets/custom-icons/brazil.svg';
import { ReactComponent as GlobalIcon } from '@assets/custom-icons/global.svg';

import './styles.scss';

export default function Wallet() {
    const { asaas, stripe, loading } = useWallets();
    const [isVisible, setIsVisible] = useState(() => {
        const stored = localStorage.getItem('header-wallet-visible');
        return stored !== null ? JSON.parse(stored) : true;
    });

    const { communities } = useCommunity();
    const isAdminAnywhere = communities?.some(c => c.isAdmin()) ?? false;

    const wallets = [
        {
            key: 'national',
            label: 'Nacional',
            currency: 'BRL',
            wallet: asaas,
            Icon: BrazilIcon,
        },
        {
            key: 'global',
            label: 'Global',
            currency: 'USD',
            wallet: stripe,
            Icon: GlobalIcon,
        },
    ];
    const hasConnectedWallet = walletIsConnected(asaas) || walletIsConnected(stripe);

    function toggleVisibility() {
        const newValue = !isVisible;
        setIsVisible(newValue);
        localStorage.setItem('header-wallet-visible', JSON.stringify(newValue));
    }

    function handleWithdraw(wallet) {
        window.navigate(walletWithdrawalPath(wallet));
    }

    if (loading) {
        return (
            <div className="cs-header-wallet">
                <Material.Skeleton
                    variant="rounded"
                    width={260}
                    height={52}
                    sx={{ borderRadius: '50px', backgroundColor: 'var(--surface-elevation)' }}
                />
            </div>
        );
    }


    if (!hasConnectedWallet) {
        if (!isAdminAnywhere) return null;
        return (
            <div className="append-account-block pl-3 my-1">
                <div className='append-account-block__info'>
                    <Text.h5 left={<IconWallet />} contrast={8} weight={400}>Cadastre sua conta</Text.h5>
                    <Text.small contrast={5} weight={400}>Adicione uma conta de recebíveis para vender seus produtos e serviços através do Ginastee</Text.small>
                </div>
                <Button
                    onClick={() => window.navigate('/banking')}
                    startIcon={<IconBuildingBank />}
                    size="small"
                    color="white"
                >
                    Cadastrar carteira
                </Button>
            </div>
        );
    }

    return (
        <div className="cs-header-wallet cs-header-wallet--active cs-header-wallet--split">
            <Button
                color="overlay"
                variant="rounded"
                className="cs-header-wallet__eye-button"
                size="micro"
                icon={isVisible ? <IconEye size={18} /> : <IconEyeOff size={18} />}
                onClick={toggleVisibility}
                tooltip={isVisible ? 'Ocultar saldos' : 'Mostrar saldos'}
            />

            <div className="cs-header-wallet__accounts">
                {wallets.map(({ key, label, currency, wallet, Icon }) => {
                    const canWithdraw = walletCanWithdraw(wallet);
                    const value = Parser.price.format(
                        walletHeadlineBalance(wallet),
                        walletBalanceCurrency(wallet, currency)
                    );

                    return (
                        <div className="cs-header-wallet__account" key={key} aria-label={`${label}: ${isVisible ? value : 'saldo oculto'}`}>
                            <span className="cs-header-wallet__account-icon">
                                <Icon />
                            </span>
                            <span className="cs-header-wallet__account-copy">
                                <Text.label contrast={9} maxLines={1}>
                                    {isVisible ? value : '••••••'}
                                </Text.label>
                            </span>
                            {canWithdraw && (
                                <Button
                                    variant="rounded"
                                    size="micro"
                                    color="overlay"
                                    icon={<IconMoneybag />}
                                    onClick={() => handleWithdraw(wallet)}
                                    tooltip={`Sacar ${label.toLowerCase()}`}
                                />
                            )}
                            {key === 'global' && (
                                <Button
                                    variant="rounded"
                                    size="micro"
                                    color="inverse"
                                    className="ml-1 open-wallet-header-btn"
                                    icon={<IconExternalLink/>}
                                    onClick={() => window.navigate('/banking/wallet')}
                                    tooltip="Abrir carteira"
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
